import { createDefaultInstrumentPatch, normalizeInstrumentPatch } from './patchSchema';
import { SY77_INSPIRED_PRESETS } from './sy77InspiredPresets';
import { CORE_PRESETS } from './corePresets';
import { ADVANCED_FM_PRESETS } from './advancedFmPresets';
import { ADVANCED_TONE_PRESETS } from './advancedTonePresets';
import { SIGNATURE_SYNTH_PRESETS } from './signatureSynthPresets';
import { DYSTOPIA_PRESETS } from './dystopiaPresets';

// One complete native catalog shared by the studio and headless edit engine.
const patchPreset = (id, name, category, changes) => {
  const base = createDefaultInstrumentPatch();
  return normalizeInstrumentPatch({
    ...base,
    ...changes,
    id,
    name,
    category,
    macros: { ...base.macros, ...(changes.macros || {}) },
    filter: { ...base.filter, ...(changes.filter || {}) },
    ampEnvelope: { ...base.ampEnvelope, ...(changes.ampEnvelope || {}) },
    performance: { ...base.performance, ...(changes.performance || {}) },
    effects: {
      ...base.effects,
      ...(changes.effects || {}),
      reverb: { ...base.effects.reverb, ...(changes.effects?.reverb || {}) },
      delay: { ...base.effects.delay, ...(changes.effects?.delay || {}) },
      chorus: { ...base.effects.chorus, ...(changes.effects?.chorus || {}) },
      distortion: { ...base.effects.distortion, ...(changes.effects?.distortion || {}) },
      compressor: { ...base.effects.compressor, ...(changes.effects?.compressor || {}) }
    },
    layers: (changes.layers || base.layers).map((layer, index) => ({ ...base.layers[index], ...layer }))
  });
};

export const FACTORY_INSTRUMENT_PRESETS = [
  createDefaultInstrumentPatch(),
  patchPreset('neon-pulse', 'Neon Pulse', 'Lead', {
    tags: ['Bright', 'Motion'],
    macros: { Punch: 0.82, Metal: 0.55, Motion: 0.72, Air: 0.64, Space: 0.24 },
    filter: { cutoff: 6200, resonance: 2.4, drive: 0.08 },
    ampEnvelope: { attack: 0.006, decay: 0.18, sustain: 0.72, release: 0.22 },
    layers: [
      { engine: 'analog', name: 'Pulse Core', waveform: 'square', level: 0.5, detune: 8, width: 0.58 },
      { engine: 'fm', name: 'Neon Edge', level: 0.28, ratio: 3, index: 2.2 },
      { engine: 'additive', name: 'Air Stack', level: 0.18, tone: 0.8 },
      { engine: 'texture', name: 'Spark', level: 0.06, density: 0.75 }
    ]
  }),
  patchPreset('cassette-clouds', 'Cassette Clouds', 'Pad', {
    tags: ['Ambient', 'Warm'],
    macros: { Punch: 0.18, Dirt: 0.38, Width: 0.88, Motion: 0.66, Air: 0.3, Space: 0.78 },
    filter: { cutoff: 1800, resonance: 0.75, drive: 0.22 },
    ampEnvelope: { attack: 0.85, decay: 1.2, sustain: 0.82, release: 3.4 },
    effects: { reverb: { enabled: true, size: 0.86, mix: 0.36 }, chorus: { enabled: true, mix: 0.4 } },
    layers: [
      { engine: 'analog', name: 'Warm Tape', waveform: 'sawtooth', level: 0.42, detune: 18, width: 0.9 },
      { engine: 'analog', name: 'Soft Octave', waveform: 'triangle', octave: 1, level: 0.16, width: 0.7 },
      { engine: 'additive', name: 'Cloud Harmonics', level: 0.22, tone: 0.35 },
      { engine: 'texture', name: 'Tape Grain', level: 0.12, density: 0.24, size: 0.8 }
    ]
  }),
  patchPreset('glass-insects', 'Glass Insects', 'SFX', {
    tags: ['Experimental', 'Metallic'],
    macros: { Punch: 0.64, Metal: 0.94, Width: 0.76, Motion: 0.86, Air: 0.8, Body: 0.22, Space: 0.48 },
    filter: { cutoff: 9600, resonance: 5.2, drive: 0.05 },
    ampEnvelope: { attack: 0.002, decay: 0.32, sustain: 0.18, release: 0.72 },
    layers: [
      { engine: 'fm', name: 'Glass Carrier', level: 0.46, ratio: 5, index: 6.4 },
      { engine: 'fm', name: 'Wing Mod', level: 0.2, octave: 1, ratio: 7, index: 3.2 },
      { engine: 'additive', name: 'Shard Stack', level: 0.25, tone: 0.9, width: 0.8 },
      { engine: 'texture', name: 'Dust', level: 0.12, density: 0.9, size: 0.15 }
    ]
  }),
  patchPreset('nylon-room', 'Nylon Room Guitar', 'Guitar', {
    tags: ['Acoustic', 'Nylon', 'Natural'],
    macros: { Punch: 0.5, Metal: 0.04, Dirt: 0.03, Width: 0.38, Motion: 0.04, Air: 0.14, Body: 0.72, Space: 0.24 },
    filter: { cutoff: 6200, resonance: 0.45, drive: 0.02, keyTrack: 0.38 },
    ampEnvelope: { attack: 0.003, decay: 1.4, sustain: 0.08, release: 0.7, velocity: 0.92 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.34, damping: 0.72, mix: 0.12 } },
    layers: [
      { engine: 'physical', physicalModel: 'pluck', name: 'Nylon & Cedar', level: 0.78, exciter: 0.52, bodySize: 0.68, bodyDecay: 0.64, material: 0.3, pickPosition: 0.32, damping: 0.46, tone: 0.54, width: 0.28, articulation: 'soft', bodyMix: 0.8, sympathetic: 0.34, coupling: 0.72, mechanics: 0.32, variation: 0.24, micPosition: 0.28 },
      { engine: 'texture', name: 'Finger', level: 0.02, density: 0.1, size: 0.07, tone: 0.58, envelope: { enabled: true, attack: 0.001, decay: 0.03, sustain: 0, release: 0.018 } },
      { enabled: false, level: 0 },
      { enabled: false, level: 0 }
    ]
  }),
  patchPreset('studio-cello', 'Studio Cello', 'Strings', {
    tags: ['Bowed', 'Warm', 'Natural'],
    macros: { Punch: 0.18, Metal: 0.08, Dirt: 0.025, Width: 0.34, Motion: 0.16, Air: 0.12, Body: 0.84, Space: 0.22 },
    filter: { cutoff: 4200, resonance: 0.6, drive: 0.02, keyTrack: 0.42 },
    ampEnvelope: { attack: 0.12, decay: 0.45, sustain: 0.82, release: 0.72, velocity: 0.72 },
    lfo: { enabled: true, shape: 'sine', rate: 5.1, depth: 0.025, sync: false, destination: 'pitch' },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.48, damping: 0.68, mix: 0.14 } },
    layers: [
      { engine: 'physical', physicalModel: 'bowed', name: 'Cello String & Body', level: 0.76, exciter: 0.55, breath: 0.36, bodySize: 0.78, bodyDecay: 0.8, material: 0.42, pickPosition: 0.12, damping: 0.46, tone: 0.48, width: 0.28, articulation: 'sustain', bodyMix: 0.82, sympathetic: 0.08, coupling: 0.68, mechanics: 0.3, variation: 0.18, micPosition: 0.42 },
      { engine: 'texture', name: 'Bow Rosin', level: 0.018, density: 0.2, size: 0.16, tone: 0.42 },
      { enabled: false, level: 0 },
      { enabled: false, level: 0 }
    ]
  }),
  patchPreset('wood-flute', 'Wood Flute', 'Winds', {
    tags: ['Breathy', 'Woodwind', 'Natural'],
    macros: { Punch: 0.12, Metal: 0.02, Dirt: 0, Width: 0.16, Motion: 0.12, Air: 0.38, Body: 0.54, Space: 0.2 },
    filter: { cutoff: 7600, resonance: 0.5, drive: 0, keyTrack: 0.56 },
    ampEnvelope: { attack: 0.07, decay: 0.28, sustain: 0.86, release: 0.3, velocity: 0.68 },
    lfo: { enabled: true, shape: 'sine', rate: 4.8, depth: 0.018, sync: false, destination: 'pitch' },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.4, damping: 0.7, mix: 0.1 } },
    layers: [
      { engine: 'physical', physicalModel: 'wind', name: 'Air Column', level: 0.72, exciter: 0.28, breath: 0.42, bodySize: 0.55, bodyDecay: 0.72, material: 0.16, damping: 0.64, width: 0.06, articulation: 'soft', bodyMix: 0.66, sympathetic: 0, coupling: 0.4, mechanics: 0.42, variation: 0.16, micPosition: 0.24 },
      { engine: 'texture', name: 'Breath', level: 0.04, density: 0.32, size: 0.55, tone: 0.68 },
      { enabled: false, level: 0 },
      { enabled: false, level: 0 }
    ]
  }),
  patchPreset('velvet-clarinet', 'Velvet Clarinet', 'Winds', {
    tags: ['Reed', 'Warm', 'Natural'],
    macros: { Punch: 0.16, Metal: 0.03, Dirt: 0, Width: 0.1, Motion: 0.08, Air: 0.2, Body: 0.68, Space: 0.18 },
    filter: { cutoff: 6200, resonance: 0.35, drive: 0, keyTrack: 0.5 },
    ampEnvelope: { attack: 0.045, decay: 0.25, sustain: 0.9, release: 0.24, velocity: 0.76 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.36, damping: 0.72, mix: 0.09 } },
    layers: [
      { engine: 'physical', physicalModel: 'reed', name: 'Grenadilla Bore', level: 0.78, exciter: 0.5, breath: 0.56, bodySize: 0.58, bodyDecay: 0.76, material: 0.38, damping: 0.42, tone: 0.5, width: 0.08, articulation: 'natural', bodyMix: 0.72, sympathetic: 0, coupling: 0.46, mechanics: 0.28, variation: 0.14, micPosition: 0.32 },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  patchPreset('warm-flugelhorn', 'Warm Flugelhorn', 'Brass', {
    tags: ['Brass', 'Warm', 'Expressive'],
    macros: { Punch: 0.24, Metal: 0.12, Dirt: 0.01, Width: 0.12, Motion: 0.08, Air: 0.16, Body: 0.78, Space: 0.18 },
    filter: { cutoff: 5200, resonance: 0.42, drive: 0.01, keyTrack: 0.48 },
    ampEnvelope: { attack: 0.035, decay: 0.2, sustain: 0.92, release: 0.28, velocity: 0.82 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.4, damping: 0.7, mix: 0.1 } },
    layers: [
      { engine: 'physical', physicalModel: 'brass', name: 'Lip & Conical Bore', level: 0.74, exciter: 0.58, breath: 0.6, bodySize: 0.66, bodyDecay: 0.78, material: 0.4, damping: 0.38, tone: 0.46, width: 0.1, articulation: 'soft', bodyMix: 0.76, sympathetic: 0, coupling: 0.5, mechanics: 0.24, variation: 0.12, micPosition: 0.4 },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  patchPreset('rosewood-marimba', 'Rosewood Marimba', 'Mallets', {
    tags: ['Mallet', 'Wood', 'Natural'],
    macros: { Punch: 0.62, Metal: 0.03, Dirt: 0, Width: 0.34, Motion: 0, Air: 0.12, Body: 0.72, Space: 0.16 },
    filter: { cutoff: 7200, resonance: 0.25, drive: 0, keyTrack: 0.5 },
    ampEnvelope: { attack: 0.001, decay: 1.8, sustain: 0, release: 0.2, velocity: 0.96 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.3, damping: 0.75, mix: 0.08 } },
    layers: [
      { engine: 'physical', physicalModel: 'mallet', name: 'Rosewood Bar', level: 0.72, exciter: 0.56, bodySize: 0.64, bodyDecay: 0.7, material: 0.42, pickPosition: 0.24, damping: 0.48, tone: 0.56, width: 0.34, articulation: 'natural', bodyMix: 0.78, sympathetic: 0.08, coupling: 0.54, mechanics: 0.22, variation: 0.2, micPosition: 0.3 },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  patchPreset('bronze-bell', 'Bronze Bell', 'Mallets', {
    tags: ['Bell', 'Metal', 'Natural'],
    macros: { Punch: 0.72, Metal: 0.68, Dirt: 0, Width: 0.58, Motion: 0, Air: 0.42, Body: 0.7, Space: 0.28 },
    filter: { cutoff: 11000, resonance: 0.28, drive: 0, keyTrack: 0.5 },
    ampEnvelope: { attack: 0.001, decay: 4.5, sustain: 0, release: 0.4, velocity: 0.95 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.5, damping: 0.66, mix: 0.13 } },
    layers: [
      { engine: 'physical', physicalModel: 'bell', name: 'Bronze Body', level: 0.62, exciter: 0.62, bodySize: 0.58, bodyDecay: 0.84, material: 0.72, pickPosition: 0.2, damping: 0.26, tone: 0.74, width: 0.56, articulation: 'bright', bodyMix: 0.84, sympathetic: 0.12, coupling: 0.58, mechanics: 0.18, variation: 0.16, micPosition: 0.42 },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  // ---------------------------------------------------------------------------
  // Patches derived from reference recordings rather than dialled by ear.
  //
  // Each was measured: fundamental by Goertzel search around the stated key,
  // partial amplitudes h1..h12 relative to the fundamental, amplitude envelope
  // from an RMS trace, and spectral centroid at the attack versus in the body
  // (which is what a filter envelope actually encodes). The numbers in the
  // comments are the measurements the settings come from, so a future edit can
  // tell an intentional voicing from a guess.
  //
  // The two phrase-based references were measured on their *first note only*.
  // Analysing a whole loop measures the arrangement, not the instrument — it
  // reports the phrase building as a ten-second attack and counts other notes
  // of the chord as partials of the first.
  // ---------------------------------------------------------------------------

  // Measured: f0 165.2Hz (E3, an octave below the stated key). Partials stay
  // strong and irregular all the way out — h3 0.89, h5 0.89, h6 0.72, h10 0.68
  // — which is the formant-rich profile of brass rather than a clean saw.
  // Envelope A 35ms / D 1.66s / S 0.04 / R 1.69s: a hit that blooms and decays,
  // not a sustain. Body is brighter than the attack (1425Hz vs 1125Hz), so the
  // filter opens into the note instead of snapping shut. Stereo width 1.15.
  patchPreset('brass-hit-e', 'Brass Hit', 'Brass', {
    tags: ['Brass', 'Hit', 'Wide'],
    macros: { Punch: 0.72, Metal: 0.58, Dirt: 0.3, Width: 0.78, Motion: 0.2, Air: 0.48, Body: 0.8, Space: 0.34 },
    filter: { type: 'lowpass', cutoff: 6000, resonance: 1.35, drive: 0.22, keyTrack: 0.4 },
    // Positive amount with a slow decay: brightness rises into the body, which
    // is what the centroid measurement shows.
    filterEnvelope: { attack: 0.09, decay: 1.1, sustain: 0.62, release: 1.2, amount: 0.5 },
    ampEnvelope: { attack: 0.035, decay: 1.66, sustain: 0.04, release: 1.69, velocity: 0.8 },
    effects: {
      reverb: { enabled: true, size: 0.5, damping: 0.45, mix: 0.2 },
      chorus: { enabled: true, rate: 0.18, depth: 0.22, mix: 0.16 },
      distortion: { enabled: true, drive: 0.2, mix: 0.22 }
    },
    layers: [
      { engine: 'analog', name: 'Brass Core', waveform: 'sawtooth', level: 0.56, detune: 14, tone: 0.6, width: 0.72 },
      // The strong, uneven upper partials are cheaper to get from FM than from
      // stacking oscillators.
      { engine: 'fm', name: 'Formant Edge', level: 0.34, ratio: 3, index: 5, tone: 0.75 },
      // brightness = 0.18 + tone*0.65 + Air*0.25; h3 needs brightness^2 ~ 0.89.
      { engine: 'additive', name: 'Odd Stack', level: 0.36, tone: 0.86, width: 0.5 },
      { engine: 'texture', name: 'Air Blow', level: 0.08, density: 0.3, size: 0.3, tone: 0.8 }
    ]
  }),

  // Measured: f0 329.3Hz (E4, exactly the stated key). Partials collapse almost
  // immediately — h2 0.79 then h3 0.10, h4 0.05, nothing above. The physical
  // string/soundboard is damped to retain that sparse spectrum instead of using
  // a bright oscillator stack. Centroid ~550Hz and flat, so the filter stays
  // low and barely moves. Envelope A 50ms / D 1.42s / S 0.04 / R 2.10s.
  patchPreset('piano-key-e', 'Soft Piano Key', 'Keys', {
    tags: ['Piano', 'Mellow', 'Clean'],
    macros: { Punch: 0.42, Metal: 0.02, Dirt: 0, Width: 0.42, Motion: 0, Air: 0.12, Body: 0.76, Space: 0.24 },
    filter: { type: 'lowpass', cutoff: 1500, resonance: 0.35, drive: 0, keyTrack: 0.5 },
    filterEnvelope: { attack: 0.02, decay: 0.9, sustain: 0.4, release: 1.4, amount: 0.18 },
    ampEnvelope: { attack: 0.05, decay: 1.42, sustain: 0.04, release: 2.1, velocity: 0.94 },
    effects: {
      reverb: { enabled: true, size: 0.38, damping: 0.68, mix: 0.11 },
      chorus: { enabled: false, rate: 0.14, depth: 0.1, mix: 0 },
      distortion: { enabled: false, drive: 0, mix: 0 },
      compressor: { enabled: true, threshold: -18, ratio: 2.5, attack: 0.01, release: 0.2 }
    },
    layers: [
      { engine: 'physical', physicalModel: 'piano', name: 'Felt Grand', level: 0.82, exciter: 0.58, bodySize: 0.72, bodyDecay: 0.78, material: 0.24, pickPosition: 0.16, damping: 0.38, tone: 0.4, width: 0.46, articulation: 'soft', bodyMix: 0.84, sympathetic: 0.72, coupling: 0.62, mechanics: 0.22, variation: 0.14, micPosition: 0.38 },
      { engine: 'texture', name: 'Key Mechanism', level: 0.012, density: 0.08, size: 0.08, tone: 0.32, envelope: { enabled: true, attack: 0.001, decay: 0.018, sustain: 0, release: 0.012 } },
      { enabled: false, level: 0 },
      { enabled: false, level: 0 }
    ]
  }),

  // Measured on the first note: f0 151.2Hz, h3 0.63 against h2 0.13 — odd/even
  // 5.51, a hollow tone dominated by fundamental and third. Energy above h4 is
  // 0.057 of the total, so it is very dark. 10.2% of the spectrum is
  // inharmonic, which is the breathy air the texture layer supplies. Attack
  // 60ms and soft.
  //
  // Not measured: sustain and release. The reference is a dense phrase whose
  // next note arrives while this one is still at 0.87 of peak, so there is no
  // observable tail. Both are voiced for a pad instead, and flagged here so a
  // later edit does not mistake them for measurements.
  patchPreset('angel-keys', 'Angel Keys', 'Pad', {
    tags: ['Soft', 'Airy', 'Emotional'],
    macros: { Punch: 0.2, Metal: 0.05, Dirt: 0.05, Width: 0.62, Motion: 0.4, Air: 0.22, Body: 0.5, Space: 0.72 },
    filter: { type: 'lowpass', cutoff: 1700, resonance: 0.5, drive: 0.04, keyTrack: 0.45 },
    filterEnvelope: { attack: 0.35, decay: 1.6, sustain: 0.55, release: 2.2, amount: 0.22 },
    ampEnvelope: { attack: 0.06, decay: 1.1, sustain: 0.62, release: 2.4, velocity: 0.6 },
    effects: {
      reverb: { enabled: true, size: 0.8, damping: 0.6, mix: 0.36 },
      chorus: { enabled: true, rate: 0.2, depth: 0.3, mix: 0.15 },
      delay: { enabled: true, time: 0.3, feedback: 0.2, mix: 0.12 },
      distortion: { enabled: false, drive: 0, mix: 0 }
    },
    layers: [
      { engine: 'analog', name: 'Soft Core', waveform: 'sine', level: 0.58, detune: 6, tone: 0.35, width: 0.55 },
      // h3 is the second-loudest partial, so it gets its own voice a fifth up
      // rather than being asked of the filter.
      { engine: 'analog', name: 'Third Voice', waveform: 'triangle', octave: 1, semitone: 7, level: 0.44, detune: 8, width: 0.6 },
      // Kept quiet: this bank's h2 was drowning the measured 0.128.
      { engine: 'additive', name: 'Halo', level: 0.04, tone: 0.2, width: 0.7 },
      { engine: 'texture', name: 'Breath', level: 0.14, density: 0.4, size: 0.7, tone: 0.5 }
    ]
  }),

  // Measured on the first note: f0 256.8Hz (C4). h3 1.64 is *louder than the
  // fundamental*, with h4 0.52, h7 0.25 and h8 0.26 still present — bright and
  // odd-leaning (2.34), with 0.29 of the energy above h4. Attack 70ms, and the
  // note is still at 0.65 of peak when the next one starts, so it sustains.
  // Stereo width 1.21.
  patchPreset('triumphant-keys', 'Triumphant Keys', 'Keys', {
    tags: ['Bright', 'Wide', 'Anthemic'],
    macros: { Punch: 0.6, Metal: 0.45, Dirt: 0.18, Width: 0.82, Motion: 0.3, Air: 0.7, Body: 0.6, Space: 0.4 },
    filter: { type: 'lowpass', cutoff: 5000, resonance: 1.1, drive: 0.12, keyTrack: 0.45 },
    filterEnvelope: { attack: 0.05, decay: 0.9, sustain: 0.6, release: 0.9, amount: 0.42 },
    ampEnvelope: { attack: 0.07, decay: 0.75, sustain: 0.65, release: 1.1, velocity: 0.75 },
    effects: {
      reverb: { enabled: true, size: 0.58, damping: 0.4, mix: 0.24 },
      chorus: { enabled: true, rate: 0.26, depth: 0.34, mix: 0.28 },
      distortion: { enabled: true, drive: 0.12, mix: 0.14 }
    },
    layers: [
      { engine: 'analog', name: 'Bright Core', waveform: 'sawtooth', level: 0.18, detune: 12, tone: 0.7, width: 0.8 },
      // The dominant third harmonic, given a voice of its own.
      { engine: 'analog', name: 'Fifth Stack', waveform: 'square', octave: 1, semitone: 7, level: 0.66, detune: 10, width: 0.75 },
      { engine: 'additive', name: 'Upper Sparkle', level: 0.3, tone: 0.88, width: 0.72 },
      { engine: 'fm', name: 'Sheen', level: 0.16, ratio: 4, index: 2.4, tone: 0.8 }
    ]
  }),

  // Reference Lab bank: independent native patches derived from coarse,
  // non-reconstructive measurements (envelope and twelve harmonic ratios).
  // No source audio, spectral frames or extracted wavetables ship with CookUp.
  patchPreset('felt-upright-reference', 'Felt Upright Reference', 'Piano', {
    tags: ['Piano', 'Felt', 'Reference Lab'],
    macros: { Punch: 0.3, Metal: 0.01, Dirt: 0.025, Width: 0.38, Motion: 0.015, Air: 0.08, Body: 0.86, Space: 0.22 },
    filter: { cutoff: 1900, resonance: 0.28, drive: 0.01, keyTrack: 0.48 },
    filterEnvelope: { attack: 0.008, decay: 0.42, sustain: 0.08, release: 1.1, amount: 0.12 },
    ampEnvelope: { attack: 0.021, decay: 0.48, sustain: 0.015, release: 1.25, velocity: 0.96 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.36, damping: 0.76, mix: 0.12 } },
    layers: [
      { engine: 'physical', physicalModel: 'piano', name: 'Felt String & Board', level: 0.8, exciter: 0.42, bodySize: 0.7, bodyDecay: 0.57, material: 0.16, pickPosition: 0.14, damping: 0.7, tone: 0.3, width: 0.4, articulation: 'soft', bodyMix: 0.88, sympathetic: 0.62, coupling: 0.66, mechanics: 0.16, variation: 0.13, micPosition: 0.38 },
      { engine: 'texture', name: 'Felt Mechanism', level: 0.01, density: 0.06, size: 0.05, tone: 0.24, envelope: { enabled: true, attack: 0.001, decay: 0.02, sustain: 0, release: 0.01 } },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  patchPreset('velvet-tine-reference', 'Velvet Tine Reference', 'Electric Piano', {
    tags: ['Tines', 'Warm', 'Reference Lab'],
    macros: { Punch: 0.34, Metal: 0.2, Dirt: 0.05, Width: 0.42, Motion: 0.16, Air: 0.1, Body: 0.7, Space: 0.2 },
    filter: { cutoff: 3100, resonance: 0.32, drive: 0.025, keyTrack: 0.5 },
    ampEnvelope: { attack: 0.011, decay: 2.15, sustain: 0.055, release: 3.8, velocity: 0.94 },
    lfo: { enabled: true, shape: 'sine', rate: 4.1, depth: 0.035, sync: false, destination: 'amplitude' },
    effects: { distortion: { enabled: false }, chorus: { enabled: true, rate: 0.12, depth: 0.12, mix: 0.08 }, reverb: { enabled: true, size: 0.42, damping: 0.74, mix: 0.1 } },
    layers: [
      { engine: 'additive', name: 'Warm Tine', level: 0.68, harmonics: [1, 0.215, 0.113, 0.006, 0.005, 0, 0, 0, 0, 0, 0, 0], harmonicTilt: 0.26, tone: 0.34, width: 0.22 },
      { engine: 'fm', name: 'Pickup Bark', level: 0.13, ratio: 2, index: 0.72, feedback: 0.02, tone: 0.38, envelope: { enabled: true, attack: 0.001, decay: 0.16, sustain: 0.03, release: 0.08 } },
      { engine: 'texture', name: 'Key Bed', level: 0.009, density: 0.08, size: 0.06, tone: 0.3, envelope: { enabled: true, attack: 0.001, decay: 0.025, sustain: 0, release: 0.01 } },
      { enabled: false, level: 0 }
    ]
  }),
  patchPreset('cedar-nylon-reference', 'Cedar Nylon Reference', 'Guitar', {
    tags: ['Nylon', 'Fingerstyle', 'Reference Lab'],
    macros: { Punch: 0.48, Metal: 0.025, Dirt: 0.01, Width: 0.3, Motion: 0.015, Air: 0.12, Body: 0.78, Space: 0.18 },
    filter: { cutoff: 4300, resonance: 0.3, drive: 0.008, keyTrack: 0.44 },
    ampEnvelope: { attack: 0.032, decay: 1.56, sustain: 0.008, release: 0.82, velocity: 0.97 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.3, damping: 0.78, mix: 0.09 } },
    layers: [
      { engine: 'physical', physicalModel: 'pluck', name: 'Nylon & Cedar', level: 0.76, exciter: 0.45, bodySize: 0.66, bodyDecay: 0.62, material: 0.24, pickPosition: 0.31, damping: 0.57, tone: 0.44, width: 0.3, articulation: 'soft', bodyMix: 0.84, sympathetic: 0.3, coupling: 0.7, mechanics: 0.24, variation: 0.18, micPosition: 0.3 },
      { engine: 'additive', name: 'Fourth Partial', level: 0.085, harmonics: [0, 0, 0, 1, 0.15, 0.16, 0.05, 0, 0.02, 0, 0, 0], harmonicTilt: 0.35, tone: 0.42, envelope: { enabled: true, attack: 0.004, decay: 0.34, sustain: 0, release: 0.12 } },
      { engine: 'texture', name: 'Finger Contact', level: 0.012, density: 0.07, size: 0.05, tone: 0.42, envelope: { enabled: true, attack: 0.001, decay: 0.018, sustain: 0, release: 0.01 } },
      { enabled: false, level: 0 }
    ]
  }),
  patchPreset('bloom-flute-reference', 'Bloom Flute Reference', 'Winds', {
    tags: ['Flute', 'Bloom', 'Reference Lab'],
    macros: { Punch: 0.08, Metal: 0.01, Dirt: 0, Width: 0.2, Motion: 0.1, Air: 0.46, Body: 0.56, Space: 0.26 },
    filter: { cutoff: 5900, resonance: 0.42, drive: 0, keyTrack: 0.58 },
    ampEnvelope: { attack: 0.92, decay: 0.24, sustain: 0.72, release: 0.58, velocity: 0.66 },
    lfo: { enabled: true, shape: 'sine', rate: 4.7, depth: 0.012, sync: false, destination: 'pitch' },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.5, damping: 0.72, mix: 0.15 } },
    layers: [
      { engine: 'physical', physicalModel: 'wind', name: 'Breath & Bore', level: 0.74, exciter: 0.23, breath: 0.52, bodySize: 0.56, bodyDecay: 0.76, material: 0.13, damping: 0.65, tone: 0.48, width: 0.12, articulation: 'soft', bodyMix: 0.68, sympathetic: 0, coupling: 0.42, mechanics: 0.3, variation: 0.17, micPosition: 0.34 },
      { engine: 'texture', name: 'Air Bloom', level: 0.025, density: 0.28, size: 0.52, tone: 0.62, envelope: { enabled: true, attack: 0.38, decay: 0.4, sustain: 0.5, release: 0.3 } },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),
  patchPreset('thumb-piano-reference', 'Thumb Piano Reference', 'Mallets', {
    tags: ['Kalimba', 'Organic', 'Reference Lab'],
    macros: { Punch: 0.65, Metal: 0.14, Dirt: 0.01, Width: 0.32, Motion: 0.015, Air: 0.1, Body: 0.74, Space: 0.2 },
    filter: { cutoff: 3900, resonance: 0.34, drive: 0.005, keyTrack: 0.5 },
    ampEnvelope: { attack: 0.001, decay: 2.2, sustain: 0, release: 0.42, velocity: 0.98 },
    effects: { distortion: { enabled: false }, chorus: { enabled: false }, reverb: { enabled: true, size: 0.36, damping: 0.78, mix: 0.1 } },
    layers: [
      { engine: 'physical', physicalModel: 'mallet', name: 'Steel Tine & Box', level: 0.72, exciter: 0.48, bodySize: 0.54, bodyDecay: 0.72, material: 0.38, pickPosition: 0.26, damping: 0.6, tone: 0.34, width: 0.32, inharmonicity: 0.08, articulation: 'muted', bodyMix: 0.82, sympathetic: 0.08, coupling: 0.56, mechanics: 0.2, variation: 0.2, micPosition: 0.28 },
      { engine: 'texture', name: 'Thumb Contact', level: 0.01, density: 0.06, size: 0.04, tone: 0.34, envelope: { enabled: true, attack: 0.001, decay: 0.014, sustain: 0, release: 0.008 } },
      { enabled: false, level: 0 }, { enabled: false, level: 0 }
    ]
  }),

  patchPreset('sub-foundation', 'Sub Foundation', 'Bass', {
    tags: ['Clean', 'Sub'],
    macros: { Punch: 0.78, Metal: 0, Dirt: 0.12, Width: 0.05, Motion: 0.05, Air: 0, Body: 0.96, Space: 0 },
    filter: { cutoff: 720, resonance: 0.55, drive: 0.15 },
    ampEnvelope: { attack: 0.004, decay: 0.2, sustain: 0.88, release: 0.16 },
    effects: { reverb: { enabled: false }, chorus: { enabled: false }, distortion: { enabled: true, mix: 0.12 } },
    layers: [
      { engine: 'analog', name: 'Sub', waveform: 'sine', octave: -1, level: 0.72, detune: 0, width: 0 },
      { engine: 'analog', name: 'Body', waveform: 'triangle', level: 0.28, detune: 0, width: 0.05 },
      { engine: 'additive', name: 'Definition', level: 0.08, tone: 0.2, width: 0 },
      { engine: 'texture', name: 'Attack', level: 0.025, density: 0.15, size: 0.1 }
    ]
  }),
  ...SY77_INSPIRED_PRESETS,
  ...CORE_PRESETS,
  ...ADVANCED_FM_PRESETS,
  ...ADVANCED_TONE_PRESETS,
  ...DYSTOPIA_PRESETS,
  ...SIGNATURE_SYNTH_PRESETS
];
// Hosted multisampled instruments (hostedInstrumentPresets.js) are not listed here: they are
// offered only once the backend confirms their library's recordings are uploaded.

